import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { resolveHolidays } from '@classy/shared';
import styles from './DayDetail.module.css';

export interface DayDetailProps {
  date: Date;
  iso: string;
  events: string[];
  birthdays: string[];
  /** When true, render the "Yesterday was…" / "Tomorrow is…" K-2 helper
   *  boxes beneath the "Today is…" headline. Toggled from Calendar
   *  settings ("K–2 helpers"). */
  showYesterdayTomorrow: boolean;
  onClose: () => void;
}

const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const CONFETTI_PIECES = 22;

/** "Friday" — used for the relative boxes and the today headline. */
function weekday(d: Date): string {
  return WEEKDAY_LONG[d.getDay()] ?? '';
}

/** "May 14, 2026" — sub-headline beneath "Today is …". */
function monthDayYear(d: Date): string {
  return `${MONTH_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function DayDetail({
  date,
  iso,
  events,
  birthdays,
  showYesterdayTomorrow,
  onClose,
}: DayDetailProps) {
  const holidays = resolveHolidays(date);
  const hasBirthdays = birthdays.length > 0;

  // Yesterday / tomorrow derived from the selected day so the popup
  // works for any day the teacher taps, not just today.
  const yesterday = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
  const tomorrow = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);

  // Pre-randomized confetti params so React doesn't reroll them on every
  // re-render. iso is part of the seed so each day's burst differs.
  const confettiSpec = useMemo(() => buildConfettiSpec(iso, CONFETTI_PIECES), [iso]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portal to document.body so position: fixed resolves against the
  // real viewport, not the KioskStage's transformed scale container.
  return createPortal(
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="day-detail-title"
      data-testid="day-detail"
      data-iso={iso}
      onClick={onClose}
    >
      <article
        className={`glass glass--solid ${styles.panel}`}
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={styles.close}
          data-testid="day-detail-close"
        >
          ×
        </button>

        <header className={styles.header}>
          <h2 id="day-detail-title" className={styles.todayLine} data-testid="day-detail-today">
            <span className={styles.todayLead}>Today is</span> {weekday(date)}
          </h2>
          <p className={styles.todayDate}>{monthDayYear(date)}</p>
          {showYesterdayTomorrow ? (
            <div className={styles.relativeRow}>
              <div
                className={`${styles.relativeBox} ${styles.relativeYesterday}`}
                data-testid="day-detail-yesterday"
              >
                <span className={styles.relativeLead}>Yesterday was</span>
                <span className={styles.relativeDate}>{weekday(yesterday)}</span>
              </div>
              <div
                className={`${styles.relativeBox} ${styles.relativeTomorrow}`}
                data-testid="day-detail-tomorrow"
              >
                <span className={styles.relativeLead}>Tomorrow is</span>
                <span className={styles.relativeDate}>{weekday(tomorrow)}</span>
              </div>
            </div>
          ) : null}
        </header>

        {holidays.federal ? (
          <p className={styles.holiday} data-testid="day-detail-federal">
            <span className={styles.pillIcon} aria-hidden="true">
              ⭐
            </span>
            {holidays.federal}
          </p>
        ) : null}
        {holidays.fun ? (
          <p className={styles.fun} data-testid="day-detail-fun">
            <span className={styles.pillIcon} aria-hidden="true">
              🎉
            </span>
            {holidays.fun}
          </p>
        ) : null}

        {events.length > 0 ? (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <span aria-hidden="true">📌</span> Events
            </h3>
            <ul className={styles.list} data-testid="day-detail-events">
              {events.map((title) => (
                <li key={title} className={styles.listItem}>
                  {title}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {hasBirthdays ? (
          <section
            className={styles.birthdaySection}
            data-testid="day-detail-birthdays"
            aria-label={`Birthdays: ${birthdays.join(', ')}`}
          >
            {/* Pure-CSS confetti burst. Each piece is a small colored
             *  square that falls + rotates; pieces overlap the heading
             *  to feel like a party. Pointer-events off so it doesn't
             *  block close clicks. */}
            <div className={styles.confetti} aria-hidden="true">
              {confettiSpec.map((piece, i) => (
                <span
                  key={i}
                  className={styles.confettiPiece}
                  style={{
                    left: `${piece.left}%`,
                    backgroundColor: piece.color,
                    width: `${piece.size}px`,
                    height: `${piece.size * 1.6}px`,
                    animationDelay: `${piece.delay}s`,
                    animationDuration: `${piece.duration}s`,
                    transform: `rotate(${piece.rotate}deg)`,
                  }}
                />
              ))}
            </div>
            <ul className={styles.birthdayList}>
              {birthdays.map((name) => (
                <li key={name} className={styles.birthdayItem}>
                  <span className={styles.balloons} aria-hidden="true">
                    🎈
                  </span>
                  Happy birthday {name}!
                  <span className={styles.balloons} aria-hidden="true">
                    🎈
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </article>
    </div>,
    document.body,
  );
}

/* ---------- Confetti spec ---------- */

interface ConfettiPiece {
  left: number;
  color: string;
  size: number;
  delay: number;
  duration: number;
  rotate: number;
}

/** Deterministic per-day confetti so re-renders don't reshuffle the
 *  scene. Seeded from the ISO date string. */
function buildConfettiSpec(seed: string, count: number): ConfettiPiece[] {
  const palette = [
    '#e89b4c', // amber (accent)
    '#0F869B', // chalkboard blue (teal)
    '#15BD6C', // chalkboard green
    '#d65d8e', // pink
    '#b89edc', // lavender
    '#f7c948', // sunny
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  function next(): number {
    h = (h * 1664525 + 1013904223) >>> 0;
    return h / 0xffffffff;
  }
  const out: ConfettiPiece[] = [];
  for (let i = 0; i < count; i++) {
    out.push({
      left: next() * 100,
      color: palette[Math.floor(next() * palette.length)]!,
      size: 6 + Math.floor(next() * 6),
      delay: next() * 2.5,
      duration: 3.2 + next() * 1.6,
      rotate: Math.floor(next() * 360),
    });
  }
  return out;
}
